import { emptyOutboxMessageCriteria } from '@src/main/modules/messages/domain/message/outbox/outbox-message-criteria.interface';
import { OutboxMessage } from '@src/main/modules/messages/domain/message/outbox/outbox-message.interface';
import { createOutboxMessageHttpRepository } from '@src/main/modules/messages/infrastructure/repository/OutboxMessageHttpRepository';
import MessageDetailModal from '@src/main/modules/messages/ui/mailbox/components/detail/MessageDetailModal';
import {
  getAttachmentColumnProps,
  getEndDateColumnProps,
  getInitDateColumnProps,
  getMessageColumnProps,
  getRecipientsColumnProps,
  getSendByColumnProps,
  getSendDateColumnProps,
  getSubjectColumnProps,
} from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxColumns';
import MailboxTable from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxTable';
import { useFormattedOutboxMessages } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useFormattedOutboxMessages';
import { useMailbox } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useMailbox';
import { useMailboxTableHeader } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useMailboxHeader';
import { useOutboxFilters } from '@src/main/modules/messages/ui/mailbox/hooks/mailbox/useMessageFilters';
import { FormattedOutboxMessage } from '@src/main/modules/messages/ui/mailbox/model/formated-message.model';
import { CustomFilterMeta } from '@src/main/modules/messages/ui/mailbox/views/mailbox/mailbox.types';
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizeOutboxMessage } from '@src/main/modules/messages/utils/formatters';
import { handleDateRangeFilter } from '@src/main/modules/messages/utils/handleDateRangeFilter';
import { mapFiltersToCriteria } from '@src/main/modules/messages/utils/mapFiltersToCriteria';
import {
  DateRangeFilter,
  makeKeyAttachmentTemplate,
} from '@src/main/modules/messages/utils/templates';
import { useAuth } from '@src/main/shared/context/AuthContext';
import InvalidDateRangeDialog from '@src/main/shared/ui/components/calendar/InvalidDateRangeDialog';
import { useDownloadFile } from '@src/main/shared/ui/hooks/useDownloadFile';
import { getThreeMonthDateRange } from '@src/main/shared/utils/dateUtils';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { DataTableFilterMeta } from 'primereact/datatable';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Outbox: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedMessage, setSelectedMessage] = useState<FormattedOutboxMessage | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [invalidDateRangeModalVisible, setInvalidDateRangeModalVisible] = useState(false);
  const [, setSelectedRange] = useState<(Date | null)[] | null>(null);
  const { startDate, endDate } = useMemo(() => getThreeMonthDateRange(), []);

  const initialFilters = useOutboxFilters(startDate, endDate);

  const filtersToCriteria = useCallback(
    (filters: CustomFilterMeta) => mapFiltersToCriteria(filters, emptyOutboxMessageCriteria),
    [],
  );

  const {
    filters,
    setFilters,
    messages,
    total,
    loadingRepository,
    pageNumber,
    pageSize,
    handlePageChange,
    sortField,
    sortOrder,
    handleSort,
  } = useMailbox<FormattedOutboxMessage>(
    user,
    createOutboxMessageHttpRepository,
    useFormattedOutboxMessages,
    filtersToCriteria,
    initialFilters,
  );

  const FILE_NAME = t('messages.mailbox.outbox.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(
    messages,
    normalizeOutboxMessage,
    FILE_NAME,
    t,
  );

  const { mutate: downloadFile } = useDownloadFile();

  const header = useMailboxTableHeader({
    initialFilters,
    setFilters,
    onExportExcel,
    onExportPdf,
  });

  const download = useCallback(
    (message: OutboxMessage) => {
      downloadFile({
        keyFile: message.keyAttachment!,
        fileName: message.attachment!,
      });
    },
    [downloadFile],
  );

  const handleSetFilters = (newFilters: DataTableFilterMeta) => {
    const updatedFilters = newFilters as CustomFilterMeta;
    setFilters(updatedFilters);
  };

  const handleRowSelect = (e: { data: FormattedOutboxMessage }) => {
    setSelectedMessage(e.data);
    setDetailModalVisible(true);
  };

  const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
    <DateRangeFilter
      value={options.value as (Date | null)[] | null}
      onChange={(val) => {
        setSelectedRange(val);
        options.filterCallback(val, options.index);
      }}
      t={t}
    />
  );

  const recipientsBodyTemplate = useCallback(
    (rowData: OutboxMessage) => rowData.recipients.join(', '),
    [],
  );

  const attachmentBodyTemplate = useCallback(
    (rowData: OutboxMessage) => makeKeyAttachmentTemplate<OutboxMessage>(t, download)(rowData),
    [t, download],
  );

  const closeDetailModal = () => {
    setDetailModalVisible(false);
  };

  if (loadingRepository) return <div>Loading...</div>;

  return (
    <div className="card">
      <MailboxTable
        messages={messages}
        filters={filters}
        setFilters={handleSetFilters}
        onFilter={(e) =>
          handleDateRangeFilter(e, setFilters, () => setInvalidDateRangeModalVisible(true))
        }
        header={header}
        handleRowSelect={handleRowSelect}
        dataKey="notificationId"
        totalRecords={total}
        first={pageNumber * pageSize}
        rows={pageSize}
        onPage={handlePageChange}
        globalFilterFields={['subject', 'sendBy', 'recipients', 'message', 'initDate', 'endDate']}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
      >
        <Column {...getSendDateColumnProps(t, dateFilterTemplate)} />
        <Column {...getSubjectColumnProps(t)} />
        <Column {...getSendByColumnProps(t)} />
        <Column {...getRecipientsColumnProps(t, recipientsBodyTemplate)} />
        <Column {...getMessageColumnProps(t)} />
        <Column {...getInitDateColumnProps(t)} />
        <Column {...getEndDateColumnProps(t)} />
        <Column {...getAttachmentColumnProps(t, attachmentBodyTemplate)} />
      </MailboxTable>
      <MessageDetailModal
        visible={detailModalVisible}
        message={selectedMessage}
        onHide={closeDetailModal}
      />
      <InvalidDateRangeDialog
        invalidDateRangeModalVisible={invalidDateRangeModalVisible}
        setInvalidDateRangeModalVisible={setInvalidDateRangeModalVisible}
        t={t}
      />
    </div>
  );
};

export default Outbox;
